import { AlertCircle, Camera, ImageUp, Keyboard } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useReducer,
  useRef,
  useState,
  type FormEvent,
} from "react";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "components/ui/sheet";
import { cn } from "utils";

import { ConnectResult } from "./ConnectResult";
import { ctaButtonClass, raisedButtonClass } from "./button-styles";
import { normalizeCode, type ScannedCode } from "./connect-code";
import {
  DetectorFailedError,
  cameraFailure,
  decodePhoto,
  detectLoop,
  loadQrDetector,
  openRearCamera,
  stopStream,
  type QrDetector,
} from "./scanner";
import {
  INITIAL_SCANNER_STATE,
  cameraWanted,
  scannerReducer,
} from "./scanner-machine";
import { useConnectionRequest } from "./use-connection-request";

/**
 * "Scan a code" (TNG-93): the camera, a photo, or the code typed in. Whichever
 * finds a code files CONNECT for this event, and the sheet shows the answer.
 */
export function ScanSheet({
  uid,
  eventId,
  open,
  onOpenChange,
}: {
  uid: string;
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="flex h-[100dvh] flex-col items-center overflow-y-auto sm:h-auto sm:max-h-[100dvh]"
        data-testid="scan-sheet"
      >
        {open && <Scanner uid={uid} eventId={eventId} />}
      </SheetContent>
    </Sheet>
  );
}

const CAMERA_MESSAGES = {
  denied:
    "The camera is blocked for this site. Allow it in the browser settings, or take a photo or type the code.",
  unavailable:
    "No camera could be opened. Take a photo or type the code instead.",
  unsupported:
    "This browser cannot use the camera here. Take a photo or type the code instead.",
  decoder:
    "The code reader could not be loaded. Check your connection and try again, or type the code.",
  ended: "The camera stopped. Try it again, or take a photo or type the code.",
  reader:
    "The code reader is not working on this camera. Take a photo or type the code instead.",
} as const;

function Scanner({ uid, eventId }: { uid: string; eventId: string }) {
  const [state, dispatch] = useReducer(scannerReducer, INITIAL_SCANNER_STATE);
  const request = useConnectionRequest(uid);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const [typed, setTyped] = useState("");
  const [typedError, setTypedError] = useState<string | null>(null);
  const inputId = useId();
  const errorId = useId();

  const submit = request.submit;
  const connect = useCallback(
    (scanned: ScannedCode) => {
      submit("CONNECT", { code: scanned.code, eventId });
    },
    [submit, eventId],
  );

  // The camera runs only in the states that want it, and every way out of
  // them (a code found, the sheet closing, the page hidden) stops the tracks.
  const wanted = cameraWanted(state);
  useEffect(() => {
    if (!wanted) return undefined;
    const controller = new AbortController();
    let stream: MediaStream | null = null;

    (async () => {
      // The decoder first: a camera that is on while nothing can read its
      // frames is the one state the sheet must never be in.
      let detector: QrDetector;
      try {
        detector = await loadQrDetector();
      } catch {
        if (!controller.signal.aborted) {
          dispatch({ type: "CAMERA_FAILED", reason: "decoder" });
        }
        return;
      }
      if (controller.signal.aborted) return;
      try {
        stream = await openRearCamera();
        if (controller.signal.aborted) {
          stopStream(stream);
          return;
        }
        streamRef.current = stream;
        // A feed that dies under the scan (another app takes the camera, the
        // OS revokes it, a USB camera is unplugged) leaves a frozen or black
        // frame and a detector reading it forever. Leaving `scanning` stops
        // the loop and offers the photo and the typed code.
        for (const track of stream.getVideoTracks()) {
          track.addEventListener(
            "ended",
            () => {
              if (!controller.signal.aborted) {
                dispatch({ type: "CAMERA_FAILED", reason: "ended" });
              }
            },
            { signal: controller.signal },
          );
        }
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play().catch(() => undefined);
        dispatch({ type: "CAMERA_READY" });
        const found = await detectLoop({
          detector,
          source: video,
          signal: controller.signal,
          ready: () => video.readyState >= 2,
        });
        if (found) {
          dispatch({ type: "FOUND", scanned: found });
          connect(found);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          dispatch({
            type: "CAMERA_FAILED",
            reason:
              error instanceof DetectorFailedError
                ? "reader"
                : cameraFailure(error),
          });
        }
      }
    })();

    const onHidden = () => {
      if (document.visibilityState === "hidden") dispatch({ type: "STOP" });
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      controller.abort();
      document.removeEventListener("visibilitychange", onHidden);
      stopStream(stream);
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [wanted, connect]);

  const onPhoto = async (file: File | undefined) => {
    if (!file) return;
    dispatch({ type: "PHOTO_CHOSEN" });
    try {
      const detector = await loadQrDetector();
      const scanned = await decodePhoto(detector, file);
      dispatch({ type: "PHOTO_DECODED", scanned });
      if (scanned) connect(scanned);
    } catch {
      dispatch({ type: "PHOTO_DECODED", scanned: null });
    }
  };

  const onTyped = (event: FormEvent) => {
    event.preventDefault();
    const code = normalizeCode(typed);
    if (!code) {
      setTypedError("A code has 8 letters and digits, like K7P4-QX9M.");
      return;
    }
    setTypedError(null);
    dispatch({ type: "ENTERED", scanned: { code } });
    connect({ code });
  };

  const again = () => {
    request.reset();
    setTyped("");
    dispatch({ type: "RESET" });
  };

  return (
    <div className="flex w-full max-w-md flex-col items-stretch gap-5 pt-6">
      <SheetHeader className="items-center text-center">
        <SheetTitle>Scan a code</SheetTitle>
        <SheetDescription>
          Point your camera at the other person&apos;s code. You both get each
          other&apos;s card straight away.
        </SheetDescription>
      </SheetHeader>

      {state.kind === "found" ? (
        <ConnectResult state={request.state} onAgain={again} />
      ) : (
        <>
          <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-border bg-hb-dusk-deep">
            {/* Muted, inline and autoplaying, or iOS opens the stream in its
                own full-screen player instead of this box. */}
            <video
              ref={videoRef}
              className="h-full w-full object-cover"
              playsInline
              muted
              autoPlay
              aria-label="Camera view"
              data-testid="scan-video"
              hidden={!wanted}
            />
            {!wanted && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-sm text-white/80">
                {state.kind === "camera-failed" ? (
                  <p role="alert" data-testid="scan-camera-error">
                    {CAMERA_MESSAGES[state.reason]}
                  </p>
                ) : state.kind === "decoding-photo" ? (
                  <p role="status">Reading the photo…</p>
                ) : state.kind === "no-code-in-photo" ? (
                  <p role="alert" data-testid="scan-photo-error">
                    No connect code in that photo. Try again closer, or type the
                    code.
                  </p>
                ) : null}
                <Button
                  type="button"
                  className={ctaButtonClass}
                  onClick={() => dispatch({ type: "START" })}
                  data-testid="scan-start"
                >
                  <Camera />
                  {state.kind === "camera-failed"
                    ? "Try the camera again"
                    : "Start camera"}
                </Button>
              </div>
            )}
          </div>

          <input
            ref={photoRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            data-testid="scan-photo-input"
            onChange={(event) => {
              void onPhoto(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => photoRef.current?.click()}
            data-testid="scan-photo"
          >
            <ImageUp />
            Take a photo
          </Button>

          <form onSubmit={onTyped} className="space-y-2" noValidate>
            <label
              htmlFor={inputId}
              className="flex items-center gap-2 text-sm font-semibold text-foreground"
            >
              <Keyboard className="h-4 w-4" aria-hidden />
              Or type the code
            </label>
            <div className="flex gap-2">
              <Input
                id={inputId}
                value={typed}
                onChange={(event) => {
                  setTyped(event.target.value);
                  setTypedError(null);
                }}
                placeholder="K7P4-QX9M"
                // 16px: iOS Safari zooms into any smaller field and stays
                // zoomed after the keyboard closes.
                className="h-11 font-mono text-base uppercase tracking-wider"
                autoCapitalize="characters"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                maxLength={12}
                aria-invalid={typedError ? true : undefined}
                aria-describedby={typedError ? errorId : undefined}
                data-testid="scan-manual-input"
              />
              <Button
                type="submit"
                className={cn("h-11 shrink-0", raisedButtonClass)}
                data-testid="scan-manual-submit"
              >
                Connect
              </Button>
            </div>
            {typedError && (
              <p
                id={errorId}
                className="flex items-start gap-2 text-sm text-foreground"
              >
                <AlertCircle
                  className="mt-0.5 h-4 w-4 shrink-0 text-destructive-text dark:text-hb-red-text"
                  aria-hidden
                />
                {typedError}
              </p>
            )}
          </form>
        </>
      )}
    </div>
  );
}
